import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { DownloadGroupName, DownloadMode, DownloadStatus } from '../reducer';
import NativeApi, { DownloadPriority } from '../../../utils/nativeApi';
import UserAgentUtils from '../../../utils/userAgent';
import DriveItemUtils from '../../../utils/driveItem';
import UrlUtils from '../../../utils/url';
import Utils, { downloadsFolder, tempFolder } from '../../../utils';
import { Api } from '../../../typings/api';
import { createNotification } from '../../NotificationBar/actions';
import ApiUtils from '../../../utils/api';
import DownloadManagerUtils from '../../../utils/downloadManager';
import { getOrFetchDriveItemMeta, updateDriveItem } from '../../DriveItemList/actions';
import {
	transformTemporaryDownload,
	updateDependents,
	startDownloadGroup,
	finishDownloadGroup,
	addItem,
	updateDownloadStatus,
	updateDownload,
	setRequestId,
	recordDownloadProgress,
	removePendingSelectedDownloadItem,
	resetTotalSelectedDownloadsInManager,
	addDownloadInProgressItem,
	removeDownloadInProgressItem,
	addDownloadInProgressDependency,
	removeDownloadInProgressDependency,
} from './store';
import StatsUtils from '../../../utils/statistics';
import allSettled from 'promise.allsettled';

import { nativeDelete } from './delete';
import { DriveItemInfo } from '../../DriveItemList/reducer';
const Q = require('q');

const requestWebDownloadItem = (driveItem: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { galleryInfos } = getState();
	const { config } = galleryInfos;

	if (Utils.isMaster(config)) {
		window.open(UrlUtils.getItemOriginalFileUrl(config, driveItem.originalFile), '_blank');
	} else {
		window.location.href = UrlUtils.getAttachmentUrl(config, driveItem.id);
	}
};

const initialProcessedIds = () => new Set<number>();

const requestMobileAppDownload = (
	driveItem: Api.DriveItem,
	requestedMode: DownloadMode,
	dependentId?: number,
	isUpdatable = false,
	isDependency = false,
	processedIds: Set<number> = initialProcessedIds(),
	isImportantOffline = false,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	return Q.Promise(async (resolve, reject) => {
		try {
			const { downloadManager } = getState();
			const itemFromDownloadManager = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id] : undefined;

			if (processedIds.has(driveItem.id)) {
				resolve();
				return;
			}

			processedIds.add(driveItem.id);

			if (requestedMode === DownloadMode.Requested && !isDependency) {
				await dispatch(addDownloadInProgressItem(driveItem.id));
			}

			if (requestedMode === DownloadMode.Dependency || isDependency) {
				await dispatch(addDownloadInProgressDependency(driveItem.id));
			}

			if (itemFromDownloadManager) {
				const { mode, version, status } = itemFromDownloadManager;

				if (status === DownloadStatus.ERROR) {
					await dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId, processedIds, isDependency));
					resolve();
					return;
				}

				if (mode === DownloadMode.Temp) {
					await dispatch(transformTemporaryDownload(driveItem, itemFromDownloadManager, dependentId));
					resolve();
					return;
				// Sometimes an item, which 'requestedMode' is 'required', can be downloaded/updated as a dependency of another item
				} else if (requestedMode === DownloadMode.Dependency || isDependency) {
					try {
						if (downloadManager.downloadInProgressDependencies && downloadManager.downloadInProgressDependencies.includes(driveItem.id)) {
							await dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
							resolve();
							return;
						}

						await dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId, processedIds, isDependency));
						await dispatch(removeDownloadInProgressDependency(driveItem.id));
					} catch (error) {
						console.error(`Download error for updatable dependency id ${driveItem.id}:`, error);
					}
					resolve();
					return;
				} else {
					if (DownloadManagerUtils.isLatestVersion(driveItem.version, version) && !isUpdatable) {
						try {
							await dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
							await dispatch(removePendingSelectedDownload(driveItem.id));
							await dispatch(removeDownloadInProgressItem(driveItem.id));
						} catch (error) {
							console.error(`Update error for item id ${driveItem.id}:`, error);
						}
						resolve();
						return;
					}

					if (isUpdatable) {

						try {
							await dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId, processedIds, isDependency, isImportantOffline));
							await dispatch(removePendingSelectedDownload(driveItem.id));
							await dispatch(removeDownloadInProgressItem(driveItem.id));
						} catch (error) {
							console.error(`Download error for updatable item id ${driveItem.id}:`, error);
						}
						resolve();
						return;
					}
				}

			} else {
				dispatch(addItem(driveItem, requestedMode, [], []));

				try {
					await dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId, processedIds, isDependency));
					await dispatch(removePendingSelectedDownload(driveItem.id));
					await dispatch(removeDownloadInProgressItem(driveItem.id));
					await dispatch(removeDownloadInProgressDependency(driveItem.id));
				} catch (error) {
					console.error(`Download error for new item id ${driveItem.id}:`, error);
				}
				resolve();
				return;
			}

			resolve();
		} catch (error) {
			console.error(`requestMobileAppDownload error for item id ${driveItem.id}:`, error);
			reject(error);
		}
	});
};

const downloadItemWithDependencies = (
	driveItem: Api.DriveItem,
	mode: DownloadMode,
	dependentId?: number,
	processedIds: Set<number> = initialProcessedIds(),
	isDependency = false,
	isImportantOffline = false,
) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const { downloadManager } = getState();
		const itemFromDownloadManager = downloadManager.items[driveItem.id];
		let allDependenciesPromise: Q.Promise<Api.Dependency[]> = Q.Promise.resolve([]);

		if (!(isDependency || isImportantOffline)) {
			allDependenciesPromise = Q.Promise.resolve(dispatch(getDependenciesWithVersions(driveItem, Api.OutcomingsLevel.ALL)));
		} else if (isImportantOffline) {
			const { dependencies } = driveItem;

			if (dependencies && dependencies.length > 0) {
				allDependenciesPromise = Q.Promise.resolve(dependencies);
			}
		}

		return allDependenciesPromise
			.then((allDependencies) => {
				const currentDependentsIds = itemFromDownloadManager ? itemFromDownloadManager.dependents : [];

				const dependentsIds: number[] = dependentId !== undefined
				&& !currentDependentsIds.includes(dependentId)
					? [dependentId, ...currentDependentsIds]
					: currentDependentsIds;

				const allDependenciesIds = !isDependency ? allDependencies.map(dependency => dependency.id) : [];
				const isLatestVersion = itemFromDownloadManager ? DownloadManagerUtils.isLatestVersion(driveItem.version, itemFromDownloadManager.version) : true;

				let promise: Q.Promise<{} | void> = Q.Promise.resolve();

				if (itemFromDownloadManager && [DownloadStatus.INIT, DownloadStatus.ERROR].includes(itemFromDownloadManager.status)) {
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.STARTING_DOWNLOAD)))
						.then(() => dispatch(nativeDownload(driveItem, mode)))
						.then(() => dispatch(updateDownload(driveItem, mode, allDependenciesIds, dependentsIds)));
				} else if (
					itemFromDownloadManager
					&& (
						!isLatestVersion
						|| ![DownloadStatus.NATIVE_DOWNLOADED].includes(itemFromDownloadManager.status)
					)
				) {
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, isLatestVersion ? DownloadStatus.RESUMING_DOWNLOAD : DownloadStatus.STARTING_UPDATE)))
						.then(() => dispatch(updateDependents(driveItem, itemFromDownloadManager, mode, dependentId)))
						.then(() => dispatch(nativeDownload(driveItem, mode, !isLatestVersion)))
						.then(() => dispatch(updateDownload(driveItem, mode, allDependenciesIds, dependentsIds)));
				} else if (itemFromDownloadManager
					&& ![
						DownloadStatus.STARTING_DOWNLOAD,
						DownloadStatus.NATIVE_DOWNLOADING,
						DownloadStatus.STARTING_UPDATE,
						DownloadStatus.UPDATING,
						DownloadStatus.RESUMING_DOWNLOAD
					].includes(itemFromDownloadManager.status)) {
					promise = promise
						.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.NATIVE_DOWNLOADED)))
						.then(() => dispatch(updateDependents(driveItem, itemFromDownloadManager, mode, dependentId)))
						.then(() => dispatch(updateDownload(driveItem, mode, allDependenciesIds, dependentsIds)));
				}

				if (!isDependency) {
					// Ensure that each dependency is processed sequentially
					promise = allDependencies.reduce((acc, updatedDependency) => {

						return acc
							.then(() => dispatch(getOrFetchDriveItemMeta(updatedDependency.id, false)))
							.then((dependency: Api.DriveItem) => {
								dependency = {
									...dependency,
									version: updatedDependency.version
								};
								const dependencyFromDownloadManager = downloadManager.items[dependency.id];
								const downloadMode = dependencyFromDownloadManager && [DownloadMode.Requested, DownloadMode.Required].includes(dependencyFromDownloadManager.mode)
									? dependencyFromDownloadManager.mode
									: DownloadMode.Dependency;

								if (dependencyFromDownloadManager) {
									return dispatch(requestMobileAppDownload(dependency, downloadMode, driveItem.id, true, true, processedIds));
								} else {
									return dispatch(requestMobileAppDownload(dependency, DownloadMode.Dependency, driveItem.id, false, true, processedIds));
								}
							});
					}, promise);
				}

				return promise;
			})
			.catch((e) => {
				console.error(`Error in downloadItemWithDependencies for item ${driveItem.id}:`, e);
				dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.ERROR));
				dispatch(createNotification('error.download_fail', { id: driveItem.id }));
				dispatch(nativeDelete(driveItem));
				return Q.Promise.reject({});
			});
	};

const nativeDownload = (driveItem: Api.DriveItem, mode: DownloadMode, isUpdating = false) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {

	const { galleryInfos } = getState();
	const { config } = galleryInfos;
	const fileName = `${driveItem.id}.zip`;
	const url = UrlUtils.getResourceZipUrl(config, driveItem.id);
	const destFolder = mode === DownloadMode.Temp ? tempFolder : downloadsFolder;
	const downloadRequest = NativeApi.downloadFile(galleryInfos.config, url, destFolder, fileName, DownloadPriority.High);

	dispatch(setRequestId(driveItem.id, downloadRequest.requestId, isUpdating));

	const minimumTimeBetweenProgress = 500;
	let lastProgressTime = 0;
	return downloadRequest.promise
		.progress(progress => {
			const isVisibleDownload = [DownloadMode.Requested, DownloadMode.Temp].includes(mode);
			const currentTime = Date.now();
			const timeSinceLastProgress = currentTime - lastProgressTime;
			const isEnoughTimeSinceLastProgress = timeSinceLastProgress > minimumTimeBetweenProgress;
			if (isEnoughTimeSinceLastProgress && isVisibleDownload) {
				lastProgressTime = currentTime;
				dispatch(recordDownloadProgress(progress, driveItem.id));
			}
		})
		.then(() => NativeApi.unzipFile(`${destFolder}/${fileName}`))
		.then(() => NativeApi.removeFile(`${destFolder}/${fileName}`))
		.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.NATIVE_DOWNLOADED)));
};

const getDependencyIds = (driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	const {
		galleryInfos : {
			config : {
				dependencies: {
					exclude: {
						ids: dependenciesExclusionIds,
						types: dependenciesExclusionTypes,
					},
				} = { exclude: { ids: [], types: [] } },
			},
		},
	} = getState();
	switch (driveItem.type) {
		case Api.DriveItemType.Publication:
		case Api.DriveItemType.Component:
		case Api.DriveItemType.Template:
		case Api.DriveItemType.Gallery:
			const response = await ApiUtils.getOutcomings(driveItem.id);
			if (response.outcomings) {
				const dependencyIds =  response.outcomings
					.filter(outcoming => !(dependenciesExclusionIds.includes(outcoming.id) || dependenciesExclusionTypes.includes(outcoming.type)))
					.map(outcoming => outcoming.id);
				return dependencyIds;
			}
			return [];
		default:
			return Q.Promise.resolve([]);
	}
};

const getDependenciesWithVersions = (driveItem: Api.DriveItem, level?: string) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<Api.Dependency[]> => {
	const {
		galleryInfos : {
			config : {
				dependencies: {
					exclude: {
						ids: dependenciesExclusionIds,
						types: dependenciesExclusionTypes,
					},
				} = { exclude: { ids: [], types: [] } },
			},
		},
	} = getState();
	switch (driveItem.type) {
		case Api.DriveItemType.Publication:
		case Api.DriveItemType.Component:
		case Api.DriveItemType.Template:
		case Api.DriveItemType.Gallery:
			const updatedDependenciesExclusionTypes = [...dependenciesExclusionTypes];

			if (!updatedDependenciesExclusionTypes.includes(Api.OutcomingType.Directory)) {
				updatedDependenciesExclusionTypes.push(Api.OutcomingType.Directory);
			}

			const response = await ApiUtils.getOutcomings(driveItem.id, level, updatedDependenciesExclusionTypes, dependenciesExclusionIds);

			if (response.outcomings) {
				let dependencyVersions: Api.Dependency[] = response.outcomings
					.filter(outcoming => !(dependenciesExclusionIds.includes(outcoming.id) || dependenciesExclusionTypes.includes(outcoming.type)))
					.map(outcoming => ({ id: outcoming.id, version: Number(outcoming.version), type: outcoming.type }));

				return dependencyVersions;
			}

			return [];
		default:
			return Q.Promise.resolve([]);
	}
};

const updateDriveItemWithDependencies = (driveItem: DriveItemInfo, isManualUpdate: boolean = false) =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {

		return dispatch(getDependenciesWithVersions(driveItem, Api.OutcomingsLevel.ALL))
			.then(dependenciesVersion => {
				if (!isManualUpdate) {
					driveItem = {
						...driveItem,
						dependencies: dependenciesVersion
					};
					void dispatch(updateDriveItem(driveItem));
					return Promise.resolve();
				} else {
					const { isInGallery } = driveItem;
					return dispatch(getOrFetchDriveItemMeta(driveItem.id, isInGallery))
						.then((updatedDriveItem: Api.DriveItem) => {
							updatedDriveItem = {
								...updatedDriveItem,
								dependencies: dependenciesVersion
							};
							void dispatch(updateDriveItem(updatedDriveItem));
						});
				}
			})
			.catch(error => {
				console.error('Failed to fetch dependencies version:', error);
			});
	};

const getInitialDependenciesUpdates = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			downloadManager: { items },
			driveList: { driveItems },
		} = getState();

		const downloadedIds = Object.keys(items)
			.filter(key => items[Number(key)].mode === DownloadMode.Requested)
			.map(Number);

		const updatePromises = downloadedIds.map(id => dispatch(updateDriveItemWithDependencies(driveItems[id])));

		await Promise.all(updatePromises);
	} catch (error) {
		console.error('Error in getInitialDependenciesUpdates:', error);
	}
};

const downloadAll = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const {
		driveList,
		galleryInfos,
		galleryInfos: {
			config: {
				applicationGallery
			}
		}
	} = getState();
	StatsUtils.trackDownloadAll();
	dispatch(startDownloadGroup(DownloadGroupName.ALL));

	const sharedDirId = Utils.getSharedDirId(driveList, galleryInfos.config);

	// TODO: Armand asked that the download all should not download the shared dir content
	return dispatch(downloadRecursively(applicationGallery, DownloadMode.Requested, true))
		.finally(() => sharedDirId && dispatch(downloadRecursively(sharedDirId, DownloadMode.Requested, true)))
		.finally(() => dispatch(finishDownloadGroup(DownloadGroupName.ALL)));
};

const downloadImportantAssets = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const {
		galleryInfos: {
			config: {
				importantAssets,
			}
		},
		driveList: {
			driveItems
		},
	} = getState();
	StatsUtils.trackDownloadImportants();
	dispatch(startDownloadGroup(DownloadGroupName.IMPORTANT));
	const enabledImportantTags = DownloadManagerUtils.getEnabledImportantTags(importantAssets);

	let importantAssetIds: string[] = [];
	if (enabledImportantTags.length > 0) {
		const allDriveItemIds = Object.keys(driveItems);
		importantAssetIds = allDriveItemIds
			.filter(DownloadManagerUtils.filterImportantItems(driveItems, enabledImportantTags));
	}

	const downloadPromises = importantAssetIds
		.map(importantAssetId => dispatch(downloadRecursively(parseInt(importantAssetId, 10), DownloadMode.Requested, false, true)));

	return allSettled(downloadPromises)
		.finally(() => dispatch(finishDownloadGroup(DownloadGroupName.IMPORTANT)));
};

const downloadSelectedAssets = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const {
		downloadManager: {
			pendingSelectedDownloads,
		},
	} = getState();
	StatsUtils.trackDownloadSelected();

	dispatch(startDownloadGroup(DownloadGroupName.SELECTED));

	const pendingSelectedDownloadsIds: string[] = Object.keys(pendingSelectedDownloads);

	const downloadPromises = pendingSelectedDownloadsIds
		.map(pendingSelectedDownloadsId =>
			dispatch(downloadRecursively(parseInt(pendingSelectedDownloadsId, 10), DownloadMode.Requested, true))
		);

	return allSettled(downloadPromises);
};

const downloadRecursively = (driveItemId: number, mode: DownloadMode, isManualUpdate: boolean = false, isImportant = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const {
			driveList: { driveItems },
			downloadManager: {
				downloadGroups,
				items,
			},
		} = getState();

		const driveItem = driveItems[driveItemId];
		const itemFromDownloadManager = items[driveItem.id] ? items[driveItem.id] : undefined;
		let promise: Q.Promise<{} | void> = Q.Promise.resolve();

		if (!driveItem && DownloadManagerUtils.getDownloadingGroup(downloadGroups) === DownloadGroupName.SELECTED) {
			dispatch(removePendingSelectedDownload(driveItemId)).then();

			return Q.Promise.resolve();
		}

		if (!driveItem) {
			dispatch(removeDownloadInProgressItem(driveItemId));

			return Q.Promise.resolve();
		}

		if (!UserAgentUtils.isOnline()) {
			dispatch(createNotification('info.offline_download_multiple_assets'));
			return Q.Promise.reject({});
		}

		const { resources } = driveItem;

		if (DriveItemUtils.isDownloadableItemType(driveItem)) {
			if (itemFromDownloadManager) {
				promise = promise
					.then(() => {
						return dispatch(requestMobileAppDownload(driveItem, mode, undefined, true, false, undefined, isImportant));
					});
			} else {
				promise = promise.then(() => dispatch(requestMobileAppDownload(driveItem, mode)));
			}
		} else {
			Q.Promise.resolve();
		}

		if (resources) {
			// Ensure that each resource is processed sequentially
			promise = resources.reduce((acc, resource) => {
				return acc.then(() => dispatch(downloadRecursively(resource.id, mode)));
			}, promise);
		}

		return promise;
	};

const removePendingSelectedDownload = (driveItemId: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const {
			downloadManager: {
				pendingSelectedDownloads,
			},
		} = getState();

		const pendingSelectedDownloadsIds = Object.keys(pendingSelectedDownloads).map(key => Number(key));

		if (pendingSelectedDownloadsIds.includes(driveItemId)) {
			return dispatch(removePendingSelectedDownloadItem(driveItemId))
				.then(() => {
					// Get the updated state after removing the item
					const state = getState();
					const pendingSelectedDownloadsIdsCount = Object.keys(state.downloadManager.pendingSelectedDownloads).length;

					if (pendingSelectedDownloadsIdsCount === 0) {
						dispatch(finishDownloadGroup(DownloadGroupName.SELECTED));
						dispatch(resetTotalSelectedDownloadsInManager());
					}
				});
		} else {
			return Promise.resolve();
		}
	};

export {
	requestWebDownloadItem,
	requestMobileAppDownload,
	downloadRecursively,
	downloadAll,
	downloadImportantAssets,
	getDependencyIds,
	getDependenciesWithVersions,
	updateDriveItemWithDependencies,
	downloadSelectedAssets,
	getInitialDependenciesUpdates,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/download.ts